import { styled } from '@mui/material';
import ThemedDialog from 'Components/_NewElements/Dialog';
import Icon from 'Components/_NewElements/Icon';
import ConfirmationDialog from 'Components/ConfirmationDialog';
import { Container, Item } from 'Components/Grid';
import InputHandler from 'Components/InputHandler';
import { partialDateFromDate } from 'neuro-utils';
import * as React from 'react';
import { useIntl } from 'react-intl';
import { attachmentFetchBuilder } from 'Store/messaging/fetchers/attachment';
import { messageFetchBuilder } from 'Store/messaging/fetchers/messages';
import { formatPartialDate } from 'Utility/date';
import { openPDFOnclick } from '.';
import colors from '../../../config/theme/colors';
import AttachmentDialog from './AttachmentDialog';

import type { TAttachmentData } from '.';
import type { Theme } from '@mui/material';
import type { IMessageListItem } from 'Store/messaging/interfaces';

const StyledDividerArea = styled('div', {
  shouldForwardProp: prop => prop !== 'compact',
})(({ theme, compact }: { theme?: Theme; compact: boolean }) => ({
  margin: compact ? '1.5rem auto 1rem auto' : '3rem auto 2rem auto',
  paddingTop: compact ? '0.5rem ' : '1rem',
  borderTop: '1px solid ' + colors.mediumGray + 'aa',
  color: theme?.palette.grey[500],
  textAlign: 'center',

  '&:first-of-type': {
    marginTop: '0rem !important',
    paddingTop: 0,
  },
}));

const StyledMessagesContainer = styled('div', {
  shouldForwardProp: prop => prop !== 'compact',
})(({ theme, compact }: { theme?: Theme; compact: boolean }) => ({
  backgroundColor: theme?.palette.grey[100] + '99',
  borderRadius: '0.4rem',
  padding: compact ? '1rem' : '2rem 1rem',
  '& > div:not(:last-child)': {
    marginBottom: compact ? '1rem' : '2rem',
  },
  '& > div:first-of-type': {
    marginTop: '0rem !important',
  },

  //minHeight: '30rem',
  maxHeight: compact ? '100%' : '40rem',
  overflowY: 'auto',
  overflowX: 'auto',
}));

const StyledSingleMessageContainer = styled(Container, {
  shouldForwardProp: prop => prop !== 'isPatient',
})(({ isPatient }: { isPatient: boolean }) => ({
  width: '100%',
  cursor: 'pointer',
  '.controls': {
    display: 'none',
  },
  ':hover': {
    //backgroundColor: !isPatient ? theme?.palette.grey[100] : 'inherit',
    '.controls': {
      display: !isPatient ? 'flex' : 'none',
    },
  },
}));

const StyledMessageControlsArea = styled(Item)(({ theme }) => ({
  '& > div': {
    cursor: 'pointer',
    padding: '0.5rem',
    ':hover': {
      backgroundColor: theme.palette.grey[300],
      borderRadius: '50rem',
    },
  },
}));

const StyledSingleMessageBody = styled(Item, {
  shouldForwardProp: prop => prop !== 'isPatient',
})(({ theme, isPatient }: { theme?: Theme; isPatient: boolean }) => ({
  backgroundColor: (isPatient ? theme?.customPalette.myms.primary.main : theme?.palette.primary.main) + '33',
  borderRadius: '0.8rem',
  padding: '0.7rem 1.3rem',
  width: 'fit-content',
  fontWeight: 400,
  borderTopRightRadius: isPatient ? '0.8rem' : 0,
  borderTopLeftRadius: isPatient ? 0 : '0.8rem',
  // So that potential multiple attachments plus a message are separated with a small margin
  '* > &:not(:last-child)': {
    marginBottom: '0.5rem',
  },
  overflowWrap: 'anywhere', // Break word if it doesn't fit on its own line
}));

const StyledTextDeleted = styled('span')(({ theme }) => ({
  color: theme.palette.grey[700],
  fontStyle: 'italic',
}));

const StyledUserSmall = styled('div', {
  shouldForwardProp: prop => prop !== 'isPatient',
})(({ theme, isPatient }: { theme?: Theme; isPatient: boolean }) => ({
  fontSize: '1.2rem',
  textAlign: isPatient ? 'start' : 'end',
  margin: isPatient ? '0 0 0 4.5rem !important' : '0 4.5rem 0 0 !important',
  color: theme?.palette.grey[700],
}));

const formatBodyText = (text: string): React.JSX.Element => {
  // Parsing a link for later
  // const urlRegexp =
  //   /(http(s)?:\/\/)?(www.)?([a-zA-Z0-9])+([-.]{1}[a-zA-Z0-9]+)*\.[a-zA-Z]{2,5}(:[0-9]{1,5})?(\/[^\s]*)?/;
  // console.log(text.split(urlRegexp));
  return <>{text}</>;
};

const SingleMessage = ({
  message,
  thread,
  isPatient,
  fm,
  resetScroll,
}: {
  message: IMessageListItem;
  thread: IThreadListItem;
  isPatient: boolean;
  fm: (id: string) => string;
  resetScroll: () => void;
}) => {
  const messageDeleted = message.deletionTimestamp;
  const attachments = messageDeleted ? [] : [...message.attachments];

  const [attachmentsData, setAttachmentsData] = React.useState<Record<string, TAttachmentData> | null>({});

  const thumbOrAttachFetches: { id: string; fetch: Promise<File | null>; type: 'pdf' | 'image' }[] = [];

  const thumbnailOrAttachFetch = (aid: string, tid: string, attach?: boolean, pdf?: boolean) => {
    const fetch = attach
      ? attachmentFetchBuilder<'fetch'>({
          name: 'fetch',
          queryParams: [thread.patientId, aid],
        })
      : attachmentFetchBuilder<'fetchThumb'>({
          name: 'fetchThumb',
          queryParams: [thread.patientId, aid, tid],
        });

    thumbOrAttachFetches.push({ id: aid, fetch, type: pdf ? 'pdf' : 'image' });
  };

  const sortOutAttachments = async () => {
    const fetches = thumbOrAttachFetches.map(t => t.fetch);

    Promise.all(fetches).then(reses => {
      let atData = {};
      reses.forEach((res, i) => {
        if (res) {
          if (thumbOrAttachFetches[i]) {
            atData = { ...atData, [thumbOrAttachFetches[i].id]: { type: thumbOrAttachFetches[i].type, blob: res } };
          }
        }
      });

      setAttachmentsData(atData);
      resetScroll();
    });
  };

  React.useEffect(() => {
    if (attachments.length > 0) {
      attachments.forEach(a => {
        if (a.contentMimeType === 'application/pdf') thumbnailOrAttachFetch(a.id, 'undefined', true, true);
        else if (a.thumbnails[0]?.id) thumbnailOrAttachFetch(a.id, a.thumbnails[0].id, false, false);
        // If theres an attachment but no thumbnail
        else thumbnailOrAttachFetch(a.id, 'undefined', true, false);
      });
      sortOutAttachments().catch(() => null);
    }
  }, []);

  const [openAttachmentId, setOpenAttachmentId] = React.useState<string | null>(null);

  const attachmentIds = attachments.map(a => a.id);

  return (
    <Container
      direction={'column'}
      alignItems={
        isPatient ? 'flex-start' : 'flex-end'
      } /** Container needed to align attachments and message to left or right side */
    >
      {attachmentIds.length > 0
        ? attachmentIds.map(a => {
            const thisAttachmentData: TAttachmentData | undefined = attachmentsData?.[a];
            const imageDimensions =
              thisAttachmentData?.type === 'image' ? { minHeight: '7rem', minWidth: '7rem', lineHeight: 0 } : {};

            return (
              <StyledSingleMessageBody
                key={a}
                isPatient={isPatient}
                onClick={
                  thisAttachmentData?.type === 'pdf'
                    ? () => openPDFOnclick(thisAttachmentData)
                    : () => setOpenAttachmentId(a)
                }
              >
                {
                  <div style={{ ...imageDimensions }}>
                    {thisAttachmentData?.type === 'image' ? (
                      <img
                        style={{ maxWidth: '100%', overflow: 'hidden' }}
                        src={URL.createObjectURL(thisAttachmentData.blob)}
                      />
                    ) : thisAttachmentData?.type === 'pdf' ? (
                      <div className="link">PDF</div>
                    ) : undefined}
                  </div>
                }
              </StyledSingleMessageBody>
            );
          })
        : undefined}
      {message.body || messageDeleted ? (
        <StyledSingleMessageBody isPatient={isPatient}>
          {messageDeleted ? (
            <StyledTextDeleted>{fm('messaging.messageDeleted')}</StyledTextDeleted>
          ) : (
            <div style={{ whiteSpace: 'pre-wrap' }}>{formatBodyText(message.body)}</div>
          )}
        </StyledSingleMessageBody>
      ) : undefined}
      <AttachmentDialog attachmentId={openAttachmentId} thread={thread} setOpenAttachmentId={setOpenAttachmentId} />
    </Container>
  );
};

const ChatView = ({ messages, thread, updateMessages, minimized = false, ended }: IChatView) => {
  const sortedMessages = messages.toSorted((msg1, msg2) => msg1.timestamp - msg2.timestamp);

  const containerRef = React.useRef<null | HTMLDivElement>(null);
  const messagesEndRef = React.useRef<null | HTMLDivElement>(null);

  const resetScroll = () => {
    //containerRef?.current?.scrollIntoView({ behavior: 'smooth' });
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 200);
  };

  React.useEffect(() => {
    resetScroll();
  }, [minimized]);

  React.useEffect(() => {
    resetScroll();
  }, [messages.length]);

  const { formatMessage } = useIntl();
  const fm = (id: string) => formatMessage({ id });

  const compact = minimized === true;

  const [editMessageData, setEditMessageData] = React.useState<{ id: string; messageString: string } | null>(null);
  const editMessage = (newMessageString: string, messageId: string) => {
    messageFetchBuilder<'update'>({
      name: 'update',
      queryParams: [thread.patientId, thread.id, messageId],
      body: newMessageString,
    }).then(res => {
      if (res) {
        setEditMessageData(null);
        updateMessages();
      }
    });
  };
  const [deleteMessage, setDeleteMessage] = React.useState<IMessageListItem | null>(null);
  const deleteMessageFunc = (message: IMessageListItem) => {
    messageFetchBuilder<'delete'>({ name: 'delete', queryParams: [thread.patientId, message.id] }).then(res => {
      if (res) {
        if (message.attachments.length > 0) {
          message.attachments.forEach(att => {
            attachmentFetchBuilder<'delete'>({ name: 'delete', queryParams: [thread.patientId, att.id] });
          });
        }
        setDeleteMessage(null);
        updateMessages();
      }
    });
  };

  const endedDate = ended && new Date(ended);
  const getTimeString = (timeDate: Date) => {
    return `${timeDate.getHours()}:${`${
      timeDate.getMinutes().toString().length === 1 ? 0 : ''
    }${timeDate.getMinutes()}`}`;
  };
  return (
    <>
      <StyledMessagesContainer ref={containerRef} compact={compact}>
        {sortedMessages.map((msg, i) => {
          const isPatient = msg.senderApplication === 'myneuro';

          const timedate = new Date(msg.timestamp);

          const getDay = (timestamp: number) => {
            return new Date(timestamp).getDay();
          };

          const dayHasChanged = sortedMessages[i - 1]?.timestamp
            ? getDay(sortedMessages[i - 1]?.timestamp ?? 0) !== getDay(msg.timestamp)
            : !sortedMessages[i - 1]?.timestamp;

          const messageDeleted = msg.deletionTimestamp;
          const lastMessage = i === sortedMessages.length - 1;
          return (
            <React.Fragment key={msg.id}>
              {dayHasChanged && (
                <StyledDividerArea compact={compact}>
                  {formatPartialDate(partialDateFromDate(timedate))}
                </StyledDividerArea>
              )}

              <StyledUserSmall
                isPatient={isPatient}
              >{`${msg.senderLastNames} ${msg.senderFirstNames}`}</StyledUserSmall>

              <StyledSingleMessageContainer
                key={msg.id}
                direction={isPatient ? 'row' : 'row-reverse'}
                alignItems={'center'}
                isPatient={isPatient}
                ref={lastMessage ? messagesEndRef : undefined}
              >
                <Item xs="auto" style={{ padding: '0 1rem' }}>
                  <Icon icon={isPatient ? 'patient' : 'org'} color={isPatient ? colors.myms.primary : undefined} />
                </Item>
                <Item xs="auto" sx={{ maxWidth: { xs: '60%', md: '69%' } }}>
                  <SingleMessage
                    message={msg}
                    thread={thread}
                    fm={fm}
                    isPatient={isPatient}
                    resetScroll={resetScroll}
                  />
                </Item>
                <Item style={{ display: 'flex', height: '100%', alignItems: 'center' }}>
                  {!messageDeleted && !ended && (
                    <StyledMessageControlsArea className="controls">
                      <div style={{ marginRight: '0.5rem' }} onClick={() => setDeleteMessage(msg)}>
                        <Icon icon="close" color={'errorDefault'} />
                      </div>
                      {msg.attachments.length === 0 && (
                        <div onClick={() => setEditMessageData({ id: msg.id, messageString: msg.body })}>
                          <Icon icon="edit" iconSize={2} />
                        </div>
                      )}
                    </StyledMessageControlsArea>
                  )}
                  {/** Todo show edit timestamp */}
                  <div style={{ fontSize: '1.4rem', padding: '0 1rem', color: colors.darkGray }}>{`${getTimeString(
                    timedate,
                  )}`}</div>
                </Item>
                <Item xs={2} />
              </StyledSingleMessageContainer>
            </React.Fragment>
          );
        })}
        {endedDate && (
          <div style={{ color: colors.gray, textAlign: 'center', marginBottom: '0' }}>
            {fm('messaging.discussionMarkedAsCompleted')}
            {`: ${formatPartialDate(partialDateFromDate(endedDate))} ${getTimeString(endedDate)}`}
          </div>
        )}
      </StyledMessagesContainer>
      <ThemedDialog
        open={!!editMessageData}
        dialogProps={{ maxWidth: 'xs' }}
        dialogActions={[
          { text: 'general.cancel', onClick: () => setEditMessageData(null), border: false },
          {
            text: 'general.save',
            onClick: () => editMessageData && editMessage(editMessageData.messageString, editMessageData.id),
          },
        ]}
        title={<div>{fm('messaging.enterNewText')}</div>}
      >
        <InputHandler
          type="TextArea"
          editing
          name={'editString'}
          formData={{
            onChange: values => {
              const newString = values['editString'];
              if (typeof newString === 'string') {
                editMessageData && setEditMessageData({ ...editMessageData, messageString: newString });
              }
            },
            document: { editString: editMessageData?.messageString },
          }}
          fullWidth
          rows={4}
          placeholder={fm('messaging.enterNewText')}
        />
      </ThemedDialog>
      <ConfirmationDialog
        open={!!deleteMessage}
        text={fm('messaging.deleteConfirmation')}
        cancel={{ callback: () => setDeleteMessage(null) }}
        confirm={{ callback: () => deleteMessage && deleteMessageFunc(deleteMessage) }}
      />
    </>
  );
};

interface IChatView {
  messages: IMessageListItem[];
  thread: IThreadListItem;
  updateMessages: () => void;
  minimized?: boolean;
  ended?: number | null;
}

export default ChatView;
